import { apiService, ApiResponse } from './api';

export type ExecutionStatus =
  | 'pending'
  | 'initializing'
  | 'running'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'security_violation'
  | 'resource_exhausted'
  | 'cancelled';

export type RuntimeType = 'python' | 'nodejs' | 'go' | 'rust';

export interface ExecutionFunctionSummary {
  rufid: string;
  rufidShort: string;
  functionName: string;
  lastStatus: ExecutionStatus;
  lastStartedAt?: string;
  lastExecutionTimeMs?: number;
  avgExecutionTimeMs?: number;
  p95ExecutionTimeMs?: number;
  errorRate: number;
  invocationsLast24h: number;
  totalExecutions: number;
  namespace?: string;
}

export interface ExecutionHistoryItem {
  executionId: string;
  rufid: string;
  functionName: string;
  status: ExecutionStatus;
  startedAt: string;
  completedAt?: string;
  executionTimeMs?: number;
  memoryUsedMb?: number;
  error?: string;
  namespace?: string;
  userId?: string;
}

export interface ExecutionFunctionList {
  items: ExecutionFunctionSummary[];
  limit: number;
  offset: number;
  total: number;
}

export interface ExecutionHistoryList {
  items: ExecutionHistoryItem[];
  limit: number;
  offset: number;
  total: number;
}

export interface ExecutionLogsResponse {
  execution_id: string;
  logs: string[];
  timestamp: string;
}

type ExecutionResponseRaw = {
  execution_id: string;
  rufid: string;
  status: ExecutionStatus;
  result?: unknown;
  error?: string;
  error_type?: string;
  logs?: string[];
  warnings?: string[];
  execution_time_ms?: number;
  memory_used_mb?: number;
  cpu_time_ms?: number;
  overhead_ms?: number;
  started_at?: string;
  completed_at?: string;
  tenant_id?: string;
  namespace?: string;
};

export interface ExecutionResponse {
  executionId: string;
  rufid: string;
  status: ExecutionStatus;
  result?: unknown;
  error?: string;
  errorType?: string;
  logs: string[];
  warnings: string[];
  executionTimeMs?: number;
  memoryUsedMb?: number;
  cpuTimeMs?: number;
  overheadMs?: number;
  startedAt?: string;
  completedAt?: string;
  tenantId?: string;
  namespace?: string;
}

export interface ExecuteFunctionPayload {
  rufid: string;
  functionName: string;
  namespace?: string;
  inputData?: Record<string, unknown>;
  runtimeType?: RuntimeType;
  asyncExecution?: boolean;
  allowNetwork?: boolean;
  allowFileSystem?: boolean;
  securityLevel?: 'MINIMAL' | 'STANDARD' | 'ENHANCED' | 'MAXIMUM' | 'PARANOID';
  maxExecutionTimeMs?: number;
  maxMemoryMb?: number;
  allowedImports?: string[];
}

type ExecutionFunctionSummaryResponse = {
  rufid: string;
  rufid_short: string;
  function_name: string;
  last_status: ExecutionStatus;
  last_started_at?: string;
  last_execution_time_ms?: number;
  avg_execution_time_ms?: number;
  p95_execution_time_ms?: number;
  error_rate: number;
  invocations_last_24h: number;
  total_executions: number;
  namespace?: string;
};

type ExecutionHistoryItemResponse = {
  execution_id: string;
  rufid: string;
  function_name: string;
  status: ExecutionStatus;
  started_at: string;
  completed_at?: string;
  execution_time_ms?: number;
  memory_used_mb?: number;
  error?: string;
  namespace?: string;
  user_id?: string;
};

type ExecutionListResponse<T> = {
  items: T[];
  limit: number;
  offset: number;
  total: number;
};

const basePath = '/execution';

function toCamelCaseSummary(item: ExecutionFunctionSummaryResponse): ExecutionFunctionSummary {
  return {
    rufid: item.rufid,
    rufidShort: item.rufid_short,
    functionName: item.function_name,
    lastStatus: item.last_status,
    lastStartedAt: item.last_started_at,
    lastExecutionTimeMs: item.last_execution_time_ms,
    avgExecutionTimeMs: item.avg_execution_time_ms,
    p95ExecutionTimeMs: item.p95_execution_time_ms,
    errorRate: item.error_rate ?? 0,
    invocationsLast24h: item.invocations_last_24h ?? 0,
    totalExecutions: item.total_executions ?? 0,
    namespace: item.namespace,
  };
}

function toCamelCaseHistory(item: ExecutionHistoryItemResponse): ExecutionHistoryItem {
  return {
    executionId: item.execution_id,
    rufid: item.rufid,
    functionName: item.function_name,
    status: item.status,
    startedAt: item.started_at,
    completedAt: item.completed_at,
    executionTimeMs: item.execution_time_ms,
    memoryUsedMb: item.memory_used_mb,
    error: item.error,
    namespace: item.namespace,
    userId: item.user_id,
  };
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      searchParams.append(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function toCamelCaseExecutionResponse(raw: ExecutionResponseRaw): ExecutionResponse {
  return {
    executionId: raw.execution_id,
    rufid: raw.rufid,
    status: raw.status,
    result: raw.result,
    error: raw.error,
    errorType: raw.error_type,
    logs: raw.logs ?? [],
    warnings: raw.warnings ?? [],
    executionTimeMs: raw.execution_time_ms,
    memoryUsedMb: raw.memory_used_mb,
    cpuTimeMs: raw.cpu_time_ms,
    overheadMs: raw.overhead_ms,
    startedAt: raw.started_at,
    completedAt: raw.completed_at,
    tenantId: raw.tenant_id,
    namespace: raw.namespace,
  };
}

class ExecutionService {
  async listFunctions(params: {
    limit?: number;
    offset?: number;
  } = {}): Promise<ApiResponse<ExecutionFunctionList>> {
    const query = buildQuery({ limit: params.limit, offset: params.offset });
    const response = await apiService.get<ExecutionListResponse<ExecutionFunctionSummaryResponse>>(
      `${basePath}/functions${query}`
    );

    if (response.status === 'success' && response.data) {
      const { items, limit = params.limit ?? 25, offset = params.offset ?? 0, total } = response.data;
      return {
        status: 'success',
        data: {
          items: items.map(toCamelCaseSummary),
          limit,
          offset,
          total,
        },
      };
    }

    return {
      status: 'error',
      error: response.error,
    };
  }

  async listHistory(params: {
    limit?: number;
    offset?: number;
    rufid?: string;
    status?: ExecutionStatus;
    startedAfter?: string;
    startedBefore?: string;
  } = {}): Promise<ApiResponse<ExecutionHistoryList>> {
    const query = buildQuery({
      limit: params.limit,
      offset: params.offset,
      rufid: params.rufid,
      status: params.status,
      started_after: params.startedAfter,
      started_before: params.startedBefore,
    });

    const response = await apiService.get<ExecutionListResponse<ExecutionHistoryItemResponse>>(
      `${basePath}/history${query}`
    );

    if (response.status === 'success' && response.data) {
      const { items, limit = params.limit ?? 50, offset = params.offset ?? 0, total } = response.data;
      return {
        status: 'success',
        data: {
          items: items.map(toCamelCaseHistory),
          limit,
          offset,
          total,
        },
      };
    }

    return {
      status: 'error',
      error: response.error,
    };
  }

  async getExecutionLogs(executionId: string): Promise<ApiResponse<ExecutionLogsResponse>> {
    return apiService.get<ExecutionLogsResponse>(`${basePath}/logs/${encodeURIComponent(executionId)}`);
  }

  async executeFunction(payload: ExecuteFunctionPayload): Promise<ApiResponse<ExecutionResponse>> {
    const response = await apiService.post<ExecutionResponseRaw>(`${basePath}/execute`, {
      rufid: payload.rufid,
      function_name: payload.functionName,
      namespace: payload.namespace,
      input_data: payload.inputData ?? {},
      runtime_type: payload.runtimeType ?? 'python',
      async_execution: payload.asyncExecution ?? false,
      allow_network: payload.allowNetwork ?? false,
      allow_file_system: payload.allowFileSystem ?? false,
      security_level: payload.securityLevel ?? 'ENHANCED',
      max_execution_time_ms: payload.maxExecutionTimeMs ?? 30000,
      max_memory_mb: payload.maxMemoryMb ?? 128,
      allowed_imports: payload.allowedImports,
    });

    if (response.status === 'success' && response.data) {
      return {
        status: 'success',
        data: toCamelCaseExecutionResponse(response.data),
      };
    }

    return {
      status: 'error',
      error: response.error,
    };
  }
}

export const executionService = new ExecutionService();
export default executionService;
